import {
  KdsCardUserActivity,
  type KdsCommentProps,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { useParams } from "react-router-dom";

import { OrderItemDetailsOrderLogReadDtoActionTypeEnum } from "@/config/apiClient";
import { ACTION_TYPES_WITH_FILES } from "@/constants/inbox.constants";
import { getHumainDurationFromNow } from "@/helpers/dates/getHumainDurationFromNow";
import { getUserFullName } from "@/helpers/getUserFullName";
import { useToasts } from "@/hooks";
import { useComments } from "@/hooks/queries/useComments";
import { userAtom } from "@/store";
import { type OrderItemPageActivityLogProps } from "./OrderItemPageActivityLog.types";
import { OrderItemPageActivityLogFiles } from "./OrderItemPageActivityLogFiles";
import { OrderItemPageActivityLogMessage } from "./OrderItemPageActivityLogMessage";
import { OrderItemPageActivityLogStep } from "./OrderItemPageActivityLogStep";

export const OrderItemPageActivityLog = ({
  orderItemLog,
  orderItemId: propsOrderItemId,
  orderId: propsOrderId,
  isChatAreaOpen = false,
}: OrderItemPageActivityLogProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const userAtomValue = useAtomValue(userAtom);

  const { orderItemId } = useParams();

  const { useAddComment } = useComments();
  const { mutateAsync: addComment } = useAddComment({
    orderId: propsOrderId,
    orderItemId: propsOrderItemId,
    orderLogId: orderItemLog.id,
  });

  // Mapping to display specific action (when a command is placed, a quote is validated, etc.)
  const orderLogActionTypeAndLabelMapper: Partial<
    Record<OrderItemDetailsOrderLogReadDtoActionTypeEnum, string>
  > = {
    [OrderItemDetailsOrderLogReadDtoActionTypeEnum.Placed]: t(
      "order.myOrdersItemDetails.activityFeed.actionLine.commandPlaced"
    ),
    [OrderItemDetailsOrderLogReadDtoActionTypeEnum.AcceptedModificationRequest]:
      t(
        "order.myOrdersItemDetails.activityFeed.actionLine.acceptedModificationRequest"
      ),
    [OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteValidated]: t(
      "order.myOrdersItemDetails.activityFeed.actionLine.quotationAgreement"
    ),
    [OrderItemDetailsOrderLogReadDtoActionTypeEnum.ConfirmProof]: t(
      "order.myOrdersItemDetails.activityFeed.actionLine.confirmProof"
    ),
    [OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaPropositionAccepted]:
      t(
        "order.myOrdersItemDetails.activityFeed.actionLine.preMediaPropositionAccepted"
      ),
    [OrderItemDetailsOrderLogReadDtoActionTypeEnum.Delivered]: t(
      "order.myOrdersItemDetails.activityFeed.actionLine.delivered"
    ),
    [OrderItemDetailsOrderLogReadDtoActionTypeEnum.Cancelled]: t(
      "order.myOrdersItemDetails.activityFeed.actionLine.cancelled"
    ),
  };

  const handleSubmitComment = async (comment: string) => {
    const trimmedComment = comment.trim();

    if (!trimmedComment) {
      add({
        state: "error",
        title: t(
          "order.myOrdersItemDetails.activityFeed.action.chatArea.errorContent"
        ),
      });
      return;
    }

    await addComment({
      content: trimmedComment,
      orderItemId: propsOrderItemId || orderItemId,
    });
  };

  return (
    <>
      {orderLogActionTypeAndLabelMapper[orderItemLog.actionType] && (
        <OrderItemPageActivityLogStep
          label={t(
            orderLogActionTypeAndLabelMapper[orderItemLog.actionType] ?? ""
          )}
          createdAt={dayjs(orderItemLog.createdAt).format("DD/MM/YYYY")}
          blocked={
            orderItemLog.actionType ===
            OrderItemDetailsOrderLogReadDtoActionTypeEnum.Cancelled
          }
        />
      )}

      {ACTION_TYPES_WITH_FILES.includes(orderItemLog.actionType) ? (
        <OrderItemPageActivityLogFiles
          orderItemLog={orderItemLog}
          handleSubmitComment={(comment) => void handleSubmitComment(comment)}
        />
      ) : (
        <KdsCardUserActivity
          avatarProps={{
            type: "image",
            name: `${orderItemLog.user?.firstname} ${orderItemLog.user?.lastname}`,
          }}
          isChatAreaOpen={isChatAreaOpen}
          chatAreaProps={{
            maxLength: 300,
            placeholder: t(
              "order.myOrdersItemDetails.activityFeed.action.chatArea.placeholder"
            ),
            onSubmit: (comment) => void handleSubmitComment(comment),
          }}
          comments={[
            ...orderItemLog.comments.map((comment): KdsCommentProps => {
              return {
                date: getHumainDurationFromNow(
                  new Date(comment.createdAt),
                  userAtomValue?.lang
                ),
                comment: comment.content,
                userName: getUserFullName({
                  firstname: comment.User?.firstname,
                  lastname: comment.User?.lastname,
                }),
                avatarProps: {
                  type: "image",
                  name: getUserFullName({
                    firstname: comment.User?.firstname,
                    lastname: comment.User?.lastname,
                  }),
                },
              };
            }),
          ]}
          HeaderReactNode={
            <OrderItemPageActivityLogMessage orderItemLog={orderItemLog} />
          }
          timePassedAfterSendingComment={getHumainDurationFromNow(
            new Date(orderItemLog.createdAt),
            userAtomValue?.lang
          )}
        />
      )}
    </>
  );
};
